import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ReportSummary } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHeader } from '@/components/generals/page-header'
import { getReports } from '@/lib/content'
import { shouldRethrow } from '@/lib/db'
import { formatDate, pad2 } from '@/lib/format'

interface FieldReportsPageProps {
    reports: Array<ReportSummary>
}

const FieldReportsPage: FC<FieldReportsPageProps> = ({ reports }): ReactNode => {
    const authors = new Set(reports.map(report => report.authorId).filter(Boolean)).size
    const latest = reports[0]

    return (
        <Layout>
            <Seo
                title='Field reports'
                description='Notes from site: short reports by the Norrvik partners on what monitored buildings actually do, from timber facades and slabs to halls and archives.'
                figure='Fig. 01 / Field reports / Notes from site'
                shape='bathhouse'
            />

            <PageHeader
                fig='01'
                title='Field reports'
                detail='Notes from site'
                note='Hover a row for its abstract'
                lines={['Notes from', 'the field.']}
                intro={(
                    <p>
                        Short reports from the studio on what monitored buildings actually do, written by the partners who measured them.
                    </p>
                )}
                readouts={[
                    { label: 'Reports', value: pad2(reports.length) },
                    { label: 'Latest', value: latest ? formatDate(latest.publishedOn) : 'Pending' },
                    { label: 'Authors', value: pad2(authors) }
                ]}
            />

            <section aria-label='Report index' className='pb-20 pt-10 desk:pb-28'>
                {reports.length === 0 ? (
                    <div className='mx-gutter border border-hairline px-6 py-16 text-center'>
                        <p className='hud text-mute'>
                            The report index is being refreshed. Please check back in a moment.
                        </p>
                    </div>
                ) : (
                    <ol className='border-t border-hairline'>
                        {reports.map(report => (
                            <li key={report.id} className='group border-b border-hairline'>
                                <Link
                                    href={`/field-reports/${report.slug}`}
                                    className='relative grid gap-3 px-gutter py-8 transition-colors duration-300 hover:bg-panel focus-visible:bg-panel desk:grid-cols-[150px_minmax(0,1fr)_220px_24px] desk:items-baseline desk:gap-8 desk:py-10'
                                >
                                    <span aria-hidden='true' className='absolute inset-y-0 left-0 w-[3px] origin-top scale-y-0 bg-signal transition-transform duration-500 ease-instrument group-hover:scale-y-100 group-focus-within:scale-y-100' />

                                    <span className='hud text-mute'>
                                        <time dateTime={report.publishedOn}>
                                            {formatDate(report.publishedOn)}
                                        </time>
                                    </span>

                                    <span className='block min-w-0'>
                                        <span className='display-md block transition-transform duration-500 ease-instrument group-hover:translate-x-4 group-focus-within:translate-x-4'>
                                            <span className='text-dim' data-mdlfy-cms={`field_reports:${report.id}:code`}>
                                                {report.code}
                                            </span>

                                            <span className='text-dim' aria-hidden='true'>
                                                &nbsp;/&nbsp;
                                            </span>

                                            <span className='text-ink' data-mdlfy-cms={`field_reports:${report.id}:title`}>
                                                {report.title}
                                            </span>
                                        </span>

                                        <span className='grid grid-rows-[0fr] transition-[grid-template-rows] duration-500 ease-instrument group-hover:grid-rows-[1fr] group-focus-within:grid-rows-[1fr] [@media(hover:none)]:grid-rows-[1fr]'>
                                            <span className='overflow-hidden'>
                                                <span className='block pt-3 text-[15px] leading-[1.6] text-ink/75 transition-transform duration-500 ease-instrument group-hover:translate-x-4 desk:truncate' data-mdlfy-cms={`field_reports:${report.id}:abstract`}>
                                                    {report.abstract}
                                                </span>
                                            </span>
                                        </span>
                                    </span>

                                    <span className='hud text-mute'>
                                        {report.authorName && report.authorId ? (
                                            <>
                                                By&nbsp;

                                                <span className='text-ink' data-mdlfy-cms={`partners:${report.authorId}:name`}>
                                                    {report.authorName}
                                                </span>
                                            </>
                                        ) : 'Norrvik studio'}
                                    </span>

                                    <ArrowUpRight
                                        className='hidden size-4 text-mute transition-transform duration-500 ease-instrument group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-ink desk:block'
                                        strokeWidth={1.5}
                                        aria-hidden='true'
                                    />
                                </Link>
                            </li>
                        ))}
                    </ol>
                )}
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<FieldReportsPageProps> = async () => {
    try {
        const reports = await getReports()

        return { props: { reports }, revalidate: 60 }
    }
    catch (error) {
        console.error('Field reports could not be loaded:', error instanceof Error ? error.message : error)

        if (shouldRethrow()) throw error

        return { props: { reports: [] }, revalidate: 30 }
    }
}

export default FieldReportsPage
